import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Flag } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

// Admin: Projekt-Phase manuell festlegen – auch ohne unterschriebenes Angebot.
export default function StageOverrideButton({ project, onDone }) {
  const [open, setOpen] = useState(false);
  const [stages, setStages] = useState([]);
  const [stage, setStage] = useState(project.tracker?.current_stage || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && !stages.length) api.get("/tracker/stages").then((r) => setStages(r.data.stages || [])).catch(() => {});
    if (open) setStage(project.tracker?.current_stage || "");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/tracker/admin-override`, { stage, reason: reason.trim() });
      toast.success(`Phase gesetzt: ${stages.find((s) => s.key === stage)?.label || stage}${data.signature_waiver ? " (ohne Unterschrift)" : ""}`);
      setOpen(false);
      setReason("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Phase setzen fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <button type="button" onClick={() => setOpen(true)} data-testid="stage-override-open"
        className="h-9 px-3 rounded-lg border border-amber-500/40 text-amber-300 text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-amber-500/10 active:scale-95 transition-transform">
        <Flag size={13} /> Phase manuell festlegen
      </button>
      <WaiverNote waiver={project.signature_waiver} />
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="max-w-md bg-[#18181B] border-zinc-800 text-zinc-100" data-testid="stage-override-dialog">
          <DialogTitle className="text-[16px] font-bold">Phase manuell festlegen</DialogTitle>
          <DialogDescription className="text-[12.5px] text-zinc-400 -mt-1">
            Setzt das Projekt direkt in die gewählte Phase. Ab „Angebot unterschrieben“ geht das auch ohne Unterschrift – der Grund wird im Verlauf gespeichert.
          </DialogDescription>
          <div>
            <label className="block text-[12px] mb-1">Phase</label>
            <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="stage-override-select"
              className="w-full h-10 rounded-lg px-3 text-[13px] bg-zinc-900 border border-zinc-700">
              {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[12px] mb-1">Grund (Pflicht)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="stage-override-reason"
              placeholder="z. B. Papier-Unterschrift liegt im Büro, wird nachgereicht"
              className="w-full rounded-lg px-3 py-2 text-[13px]" />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setOpen(false)} disabled={busy} data-testid="stage-override-cancel"
              className="h-10 px-4 rounded-lg bg-zinc-800 border border-zinc-700 text-[13px] font-semibold hover:bg-zinc-700">
              Abbrechen
            </button>
            <button type="button" onClick={submit} disabled={busy || !stage || reason.trim().length < 5} data-testid="stage-override-confirm"
              className="flex-1 h-10 rounded-lg bg-amber-500 text-black font-bold text-[13px] inline-flex items-center justify-center gap-2 hover:bg-amber-400 disabled:opacity-50">
              {busy && <Loader2 size={14} className="animate-spin" />} Phase setzen
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WaiverNote({ waiver }) {
  if (!waiver) return null;
  return (
    <span className="text-[11.5px] text-amber-300/90" data-testid="stage-override-waiver-note">
      Ohne Unterschrift freigegeben am {new Date(waiver.at).toLocaleDateString("de-DE")}
      {waiver.by_name ? ` durch ${waiver.by_name}` : ""} · {waiver.reason}
    </span>
  );
}
